import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import RouteMapLeaflet from "@/components/RouteMapLeaflet";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  X, Loader2, Route, AlertTriangle, ArrowUp, ArrowDown, Truck, Clock,
  MapPin, Coffee, Save, Wand2, Ban, Info, Plus, Trash2,
} from "lucide-react";

let HQ = "FY4 2RP";
const hrs = (m) => `${Math.floor(m / 60)}h ${m % 60}m`;
const emptyDay = (date) => ({ date, start_time: "08:00", end_time: "17:00", start_postcode: HQ, end_postcode: HQ, break_mins: 30, van_title: "", driver: "" });

export default function RunPlanner({ shiftIds, onClose, onSaved }) {
  const [name, setName] = useState("New Run");
  const [days, setDays] = useState(() => {
    const base = new Date(); base.setDate(base.getDate() + 7);
    return [0, 1, 2].map((i) => { const d = new Date(base); d.setDate(d.getDate() + i); return emptyDay(d.toISOString().slice(0, 10)); });
  });
  const [fixed, setFixed] = useState({});
  const [assignment, setAssignment] = useState(null);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [saving, setSaving] = useState(false);
  const [activeDay, setActiveDay] = useState(0);

  useEffect(() => {
    api.get("/company/hq").then(({ data }) => {
      if (!data?.postcode || data.postcode === HQ) return;
      const old = HQ;
      HQ = data.postcode;
      setDays((ds) => ds.map((d) => ({ ...d, start_postcode: d.start_postcode === old ? HQ : d.start_postcode, end_postcode: d.end_postcode === old ? HQ : d.end_postcode })));
    }).catch(() => {});
  }, []);

  const setDay = (i, k, v) => setDays((ds) => ds.map((d, j) => (j === i ? { ...d, [k]: v } : d)));
  const resetPreview = () => { setPreview(null); setAssignment(null); setActiveDay(0); };
  const addDay = () => {
    setDays((ds) => {
      const last = ds[ds.length - 1];
      const nd = new Date((last?.date || new Date().toISOString().slice(0, 10)) + "T00:00:00");
      nd.setDate(nd.getDate() + 1);
      return [...ds, emptyDay(nd.toISOString().slice(0, 10))];
    });
    resetPreview();
  };
  const removeDay = (i) => { setDays((ds) => (ds.length <= 1 ? ds : ds.filter((_, j) => j !== i))); resetPreview(); };

  const runPlan = async (asg) => {
    setBusy(true);
    try {
      const body = { name, shift_ids: shiftIds, days, fixed_times: fixed };
      if (asg) body.assignment = asg;
      const r = await api.post("/runs/plan", body);
      setPreview(r.data);
      const derived = {}; r.data.days.forEach((d, i) => { derived[String(i)] = d.legs.map((l) => l.shift_id); });
      setAssignment(derived);
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Could not plan run"); }
    finally { setBusy(false); }
  };

  const recalc = (asg) => { setAssignment(asg); runPlan(asg); };

  const moveWithin = (dayIdx, from, dir) => {
    const asg = { ...assignment }; const arr = [...asg[String(dayIdx)]];
    const to = from + dir; if (to < 0 || to >= arr.length) return;
    [arr[from], arr[to]] = [arr[to], arr[from]]; asg[String(dayIdx)] = arr; recalc(asg);
  };
  const moveToDay = (fromDay, sid, toDay) => {
    if (fromDay === toDay) return;
    const asg = { ...assignment };
    asg[String(fromDay)] = (asg[String(fromDay)] || []).filter((x) => x !== sid);
    asg[String(toDay)] = [...(asg[String(toDay)] || []), sid]; recalc(asg);
  };
  const addUnassigned = (sid, toDay) => {
    const asg = { ...assignment }; asg[String(toDay)] = [...(asg[String(toDay)] || []), sid]; recalc(asg);
  };

  const save = async () => {
    setSaving(true);
    try {
      const body = { name, shift_ids: shiftIds, days, fixed_times: fixed, assignment };
      const r = await api.post("/runs", body);
      toast.success(`Run saved · ${r.data.linked} shifts linked${r.data.unassigned.length ? ` · ${r.data.unassigned.length} unassigned` : ""}`);
      onSaved && onSaved(r.data.run_id);
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Could not save run"); }
    finally { setSaving(false); }
  };

  const dayNames = useMemo(() => days.map((d) => new Date(d.date + "T00:00:00").toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short" })), [days]);
  const t = preview?.traffic;

  return (
    <div className="fixed inset-0 z-[80] bg-slate-900/70 backdrop-blur-sm flex items-start justify-center p-3 overflow-y-auto" data-testid="run-planner">
      <div className="relative bg-slate-50 rounded-2xl shadow-2xl max-w-6xl w-full my-4">
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200 bg-white rounded-t-2xl sticky top-0 z-10">
          <div className="flex items-center gap-2.5">
            <span className="w-9 h-9 rounded-xl bg-indigo-50 text-indigo-600 flex items-center justify-center"><Route className="w-5 h-5" /></span>
            <div>
              <h2 className="text-base font-bold text-slate-900">Run Planner</h2>
              <p className="text-xs text-slate-500">{shiftIds.length} shifts selected · preview only — nothing is changed until you save</p>
            </div>
          </div>
          <button onClick={onClose} className="p-1 text-slate-400 hover:text-slate-700" data-testid="run-close"><X className="w-5 h-5" /></button>
        </div>

        <div className="p-5 space-y-5">
          {/* Config */}
          <div className="bg-white border border-slate-200 rounded-xl p-4">
            <div className="flex items-center gap-3 mb-3">
              <label className="text-xs font-semibold uppercase text-slate-500">Run name</label>
              <Input value={name} onChange={(e) => setName(e.target.value)} className="max-w-xs h-9" data-testid="run-name" />
              <span className="text-xs text-slate-400">{days.length} day{days.length === 1 ? "" : "s"}</span>
              <Button variant="outline" size="sm" onClick={addDay} className="ml-auto h-8 gap-1.5" data-testid="run-add-day"><Plus className="w-3.5 h-3.5" /> Add day</Button>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
              {days.map((d, i) => (
                <div key={i} className="border border-slate-200 rounded-lg p-3 space-y-2" data-testid={`run-day-config-${i}`}>
                  <div className="flex items-center justify-between">
                    <div className="text-xs font-bold text-slate-700">Day {i + 1}</div>
                    {days.length > 1 && <button onClick={() => removeDay(i)} data-testid={`run-remove-day-${i}`} className="p-1 rounded text-slate-300 hover:text-rose-600 hover:bg-rose-50 transition-colors" title="Remove day"><Trash2 className="w-3.5 h-3.5" /></button>}
                  </div>
                  <input type="date" value={d.date} onChange={(e) => setDay(i, "date", e.target.value)} className="w-full h-8 rounded-md border border-slate-200 bg-slate-50 px-2 text-sm" data-testid={`run-day-date-${i}`} />
                  <div className="grid grid-cols-2 gap-2">
                    <label className="text-[11px] text-slate-500">Start<input type="time" value={d.start_time} onChange={(e) => setDay(i, "start_time", e.target.value)} className="w-full h-8 rounded-md border border-slate-200 bg-slate-50 px-2 text-sm" /></label>
                    <label className="text-[11px] text-slate-500">Finish<input type="time" value={d.end_time} onChange={(e) => setDay(i, "end_time", e.target.value)} className="w-full h-8 rounded-md border border-slate-200 bg-slate-50 px-2 text-sm" /></label>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <label className="text-[11px] text-slate-500">Start postcode<Input value={d.start_postcode} onChange={(e) => setDay(i, "start_postcode", e.target.value)} className="h-8" /></label>
                    <label className="text-[11px] text-slate-500">End postcode<Input value={d.end_postcode} onChange={(e) => setDay(i, "end_postcode", e.target.value)} className="h-8" /></label>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <label className="text-[11px] text-slate-500">Van<Input value={d.van_title} onChange={(e) => setDay(i, "van_title", e.target.value)} className="h-8" placeholder="Van" /></label>
                    <label className="text-[11px] text-slate-500">Break (min)<Input type="number" value={d.break_mins} onChange={(e) => setDay(i, "break_mins", Number(e.target.value))} className="h-8" /></label>
                  </div>
                  <label className="text-[11px] text-slate-500 block">Driver<Input value={d.driver} onChange={(e) => setDay(i, "driver", e.target.value)} className="h-8" placeholder="Driver" /></label>
                </div>
              ))}
            </div>
            <div className="flex items-center gap-2 mt-3">
              <Button onClick={() => runPlan(null)} disabled={busy} className="gap-1.5 bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="run-optimise">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Wand2 className="w-4 h-4" />} Propose efficient order
              </Button>
              {preview && <span className="text-xs text-slate-400">Reorder or move stops below, then Save</span>}
            </div>
          </div>

          {preview && (
            <>
              {/* Traffic banner */}
              <div className={`rounded-xl border p-3 flex items-start gap-2.5 text-sm ${t.used_google ? "border-emerald-200 bg-emerald-50 text-emerald-800" : "border-amber-200 bg-amber-50 text-amber-800"}`} data-testid="run-traffic-banner">
                <Info className="w-4 h-4 mt-0.5 shrink-0" /> <span>{t.note}</span>
              </div>

              {/* Unassigned */}
              {preview.unassigned.length > 0 && (
                <div className="rounded-xl border border-rose-200 bg-rose-50 p-4" data-testid="run-unassigned">
                  <h3 className="text-sm font-bold text-rose-800 flex items-center gap-1.5 mb-2"><Ban className="w-4 h-4" /> {preview.unassigned.length} shift(s) could not be fitted</h3>
                  <ul className="space-y-1.5">
                    {preview.unassigned.map((u) => (
                      <li key={u.shift_id} className="flex items-center justify-between gap-2 text-sm">
                        <span className="text-rose-700"><span className="font-mono text-xs">{u.shift_id}</span> — {u.reason}</span>
                        <select onChange={(e) => e.target.value !== "" && addUnassigned(u.shift_id, Number(e.target.value))} defaultValue="" className="text-xs rounded border border-rose-200 bg-white px-2 py-1" data-testid={`run-unassigned-add-${u.shift_id}`}>
                          <option value="">Force into…</option>
                          {days.map((d, i) => <option key={i} value={i}>Day {i + 1}</option>)}
                        </select>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Day tabs */}
              <div className="flex items-center gap-2">
                {preview.days.map((d, i) => (
                  <button key={i} onClick={() => setActiveDay(i)} data-testid={`run-day-tab-${i}`}
                    className={`px-3 py-1.5 rounded-lg text-sm font-medium border ${activeDay === i ? "bg-indigo-600 text-white border-indigo-600" : "bg-white text-slate-600 border-slate-200"}`}>
                    {dayNames[i]} · {d.legs.length}
                  </button>
                ))}
              </div>

              {preview.days[activeDay] && (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  {/* Timetable */}
                  <div className="bg-white border border-slate-200 rounded-xl p-4">
                    {(() => { const dd = preview.days[activeDay]; const tot = dd.totals; return (
                      <>
                        <div className="grid grid-cols-4 gap-2 mb-3 text-center">
                          <Tot icon={Clock} label="Driving" val={hrs(tot.drive_mins)} />
                          <Tot icon={Truck} label="On-site" val={hrs(tot.onsite_mins)} />
                          <Tot icon={MapPin} label="Mileage" val={`${tot.miles} mi`} />
                          <Tot icon={Coffee} label="Finish" val={tot.finish_time} />
                        </div>
                        {dd.legs.length === 0 ? <p className="text-sm text-slate-400 text-center py-6">No stops on this day.</p> : (
                          <div className="space-y-2" data-testid={`run-timetable-${activeDay}`}>
                            {dd.legs.map((l, idx) => (
                              <div key={l.shift_id} className={`rounded-lg border p-2.5 ${l.late_for_fixed ? "border-amber-300 bg-amber-50" : "border-slate-200"}`} data-testid={`run-leg-${l.shift_id}`}>
                                <div className="flex items-start gap-2">
                                  <span className="w-6 h-6 rounded-full bg-indigo-100 text-indigo-700 text-xs font-bold flex items-center justify-center shrink-0 mt-0.5">{idx + 1}</span>
                                  <div className="min-w-0 flex-1">
                                    {l.task_legacy_id ? (
                                      <a href={`/tasks/${l.task_legacy_id}`} target="_blank" rel="noreferrer" className="text-sm font-semibold text-indigo-700 hover:underline truncate block" data-testid={`run-leg-link-${l.shift_id}`}>{l.title}</a>
                                    ) : (
                                      <div className="text-sm font-semibold text-slate-800 truncate">{l.title}</div>
                                    )}
                                    <div className="text-[11px] text-slate-400">{l.postcode}{l.fixed_time ? ` · fixed ${l.fixed_time}` : ""}</div>
                                    <div className="text-xs text-slate-600 mt-1 flex flex-wrap gap-x-3 gap-y-0.5">
                                      <span>drive {l.travel_mins}m ({l.travel_miles}mi, <em>{l.travel_source}</em>)</span>
                                      <span>arr <b>{l.arrival}</b></span>
                                      {l.break_mins > 0 && <span className="text-amber-600">+break {l.break_mins}m</span>}
                                      <span>on-site {l.onsite_start}–{l.departure}</span>
                                    </div>
                                    {l.late_for_fixed && <div className="text-[11px] text-amber-700 mt-0.5">⚠ Arrives after the fixed {l.fixed_time} appointment</div>}
                                  </div>
                                  <div className="flex flex-col items-center gap-1 shrink-0">
                                    <button onClick={() => moveWithin(activeDay, idx, -1)} className="p-1 rounded text-slate-400 hover:text-indigo-600 hover:bg-indigo-50" data-testid={`run-leg-up-${l.shift_id}`}><ArrowUp className="w-3.5 h-3.5" /></button>
                                    <button onClick={() => moveWithin(activeDay, idx, 1)} className="p-1 rounded text-slate-400 hover:text-indigo-600 hover:bg-indigo-50" data-testid={`run-leg-down-${l.shift_id}`}><ArrowDown className="w-3.5 h-3.5" /></button>
                                    <select value={activeDay} onChange={(e) => moveToDay(activeDay, l.shift_id, Number(e.target.value))} className="text-[10px] rounded border border-slate-200 bg-white w-9" title="Move to day" data-testid={`run-leg-day-${l.shift_id}`}>
                                      {days.map((_, i) => <option key={i} value={i}>D{i + 1}</option>)}
                                    </select>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </>
                    ); })()}
                  </div>
                  {/* Map */}
                  <div className="bg-white border border-slate-200 rounded-xl p-4">
                    <RouteMapLeaflet legs={preview.days[activeDay].legs} testid="run-map" />
                  </div>
                </div>
              )}

              <div className="flex items-center justify-end gap-2 sticky bottom-0 bg-slate-50 py-3">
                <Button variant="outline" onClick={onClose}>Cancel</Button>
                <Button onClick={save} disabled={saving} className="gap-1.5 bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="run-save">
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save run & link shifts
                </Button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function Tot({ icon: Icon, label, val }) {
  return (
    <div className="rounded-lg bg-slate-50 border border-slate-100 p-2">
      <Icon className="w-3.5 h-3.5 text-indigo-500 mx-auto" />
      <div className="text-[10px] uppercase text-slate-400 mt-0.5">{label}</div>
      <div className="text-sm font-bold text-slate-800">{val}</div>
    </div>
  );
}
